"use client"

import { usePage } from "@inertiajs/react"
import "maplibre-gl/dist/maplibre-gl.css"
import { useEffect, useRef, useState } from "react"

import { RAIL_WIDTH } from "@/components/console/rail"
import { Dropdown } from "@/components/ui/dropdown-menu"
import { LG, useMedia } from "@/hooks/use-media"

import { GroundEngine, MOTION, reducedMotion } from "./engine"
import { driveEstimate, mapsDirectionsUrl } from "./geo"
import { GroundStill } from "./still"
import {
  type GroundMode,
  type GroundOffice,
  type GroundSpec,
  type GroundSubject,
  useGroundSpec,
  useGroundStore,
} from "./store"

/*
 * The ground layer (Master Design System §09 · §12): fixed right of the rail,
 * under the sheet. Bottom → top: the live map (when a page has handed it a
 * point) · the constructed still · the paper-grain multiply and the one lamp,
 * which are fixtures and never move · the band above the sheet with the
 * address, the drive and the directions · the hint when the sheet is docked.
 * The still is under every console page; the live map only where a page has
 * a point. Below lg the rail folds into a bar and the sheet becomes the page,
 * so the ground is not drawn there.
 */
export function Ground() {
  const spec = useGroundSpec()
  const { maps } = usePage().props
  const lg = useMedia(LG)
  const apiKey = maps?.key ?? null
  // below lg the sheet is the page: no layer, and no map paid for behind it
  if (!spec || !lg) return null
  return <GroundLayer spec={spec} apiKey={apiKey} />
}

const CSS_EASE_OUT = "cubic-bezier(0,0,.2,1)"

function GroundLayer({
  spec,
  apiKey,
}: {
  spec: GroundSpec
  apiKey: string | null
}) {
  const mode = useGroundStore((s) => s.mode)
  const setMode = useGroundStore((s) => s.setMode)
  const origin = useGroundStore((s) => s.origin)
  const setOrigin = useGroundStore((s) => s.setOrigin)
  const mapRef = useRef<HTMLDivElement>(null)
  const stillRef = useRef<HTMLDivElement>(null)
  const engineRef = useRef<GroundEngine | null>(null)
  const modeBefore = useRef<GroundMode>("sheet")

  const subject = spec.kind === "resolve" ? spec : null

  // the engine lives as long as the layer does
  useEffect(() => {
    if (!mapRef.current || !stillRef.current) return
    const engine = new GroundEngine(mapRef.current, stillRef.current, () => {
      // clicking territory (not markers, not chips) expands the ground
      const state = useGroundStore.getState()
      if (state.mode === "sheet" && state.spec?.kind === "resolve") {
        state.setMode("expanded")
      }
    })
    engineRef.current = engine
    return () => {
      engine.destroy()
      engineRef.current = null
    }
  }, [])

  // what the page asked for: a still, or a point to resolve onto
  const specKey = JSON.stringify(spec)
  useEffect(() => {
    const engine = engineRef.current
    if (!engine) return
    const wanted = JSON.parse(specKey) as GroundSpec
    if (wanted.kind === "resolve") engine.resolveOnto(wanted, apiKey)
    else engine.retreat()
  }, [specKey, apiKey])

  // how the sheet sits: over the ground, docked, or docked with directions
  // open — and where the drive starts: the job's office, or the one chosen
  // for this visit
  const office = origin ?? subject?.office ?? null
  useEffect(() => {
    const engine = engineRef.current
    const was = modeBefore.current
    modeBefore.current = mode
    if (!engine) return
    if (mode === "expanded") engine.expand()
    else if (mode === "directions") engine.directions(office)
    else if (was !== "sheet") engine.restore()
  }, [mode, office])

  const drive = office ? driveEstimate(office, subject!.point) : null

  return (
    <div
      className="fixed inset-y-0 right-0 z-0 hidden lg:block"
      style={{ left: RAIL_WIDTH }}
    >
      {/* sized in flow, not absolutely: MapLibre's own stylesheet sets its container `position: relative` */}
      <div
        ref={mapRef}
        className="h-full w-full"
        aria-label={
          subject ? `Map — ${subject.label} and surrounds` : undefined
        }
      />
      <div ref={stillRef} className="ground-still absolute inset-0 z-10">
        <GroundStill />
      </div>
      <span
        aria-hidden="true"
        className="ground-grain pointer-events-none absolute inset-0 z-20"
      />
      <span
        aria-hidden="true"
        className="ground-lamp pointer-events-none absolute inset-0 z-20"
      />

      {subject && (
        <Band
          key={specKey}
          subject={subject}
          office={office}
          chosen={origin !== null}
          drive={drive}
          mode={mode}
          onRecenter={() => engineRef.current?.recenter(mode !== "sheet")}
          onDirections={() => setMode("directions")}
          onOrigin={setOrigin}
          onClose={() => setMode("sheet")}
        />
      )}

      {mode !== "sheet" && <Hint />}
    </div>
  )
}

/** Arrives a beat after mount: `true` once `delay` has passed (at once under reduced motion). */
function useArrival(delay: number) {
  const [arrived, setArrived] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setArrived(true), reducedMotion() ? 0 : delay)
    return () => clearTimeout(t)
  }, [delay])
  return arrived
}

/** Shown while the sheet is docked: the strip at the desk edge is the handle. */
function Hint() {
  const shown = useArrival(300)
  return (
    <div
      className="pointer-events-none absolute bottom-[66px] left-1/2 z-40 -translate-x-1/2"
      style={{ opacity: shown ? 1 : 0, transition: "opacity 200ms linear" }}
    >
      <span className="map-chip text-secondary tracking-chip font-mono text-[9.5px] leading-none font-medium">
        job sheet ▾ · click it to bring it back
      </span>
    </div>
  )
}

/*
 * The band: the ~150px of clear ground above the sheet on a subject page. The
 * address is a mappable point (click re-centres); the drive is an estimate and
 * says so; "directions ↗" starts at your office. Everything here is paper
 * chrome — stone.50 at 94% with shadow.lift — furniture, not product.
 */
function Band({
  subject,
  office,
  chosen,
  drive,
  mode,
  onRecenter,
  onDirections,
  onOrigin,
  onClose,
}: {
  subject: GroundSubject
  /** Where the drive starts: the job's office, or the one chosen for the visit. */
  office: GroundOffice | null
  chosen: boolean
  drive: ReturnType<typeof driveEstimate> | null
  mode: GroundMode
  onRecenter: () => void
  onDirections: () => void
  onOrigin: (office: GroundOffice) => void
  onClose: () => void
}) {
  // the band's chips are DOM and arrive with the show leaf, not the pin
  const shown = useArrival(MOTION.leafInDelay)
  const directions = mode === "directions"
  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 z-30 h-(--spacing-ground-band)"
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(24px)",
        transition: `transform ${MOTION.leafIn}ms ${CSS_EASE_OUT}, opacity ${MOTION.leafIn}ms ${CSS_EASE_OUT}`,
      }}
    >
      {!directions && subject.address && (
        <button
          type="button"
          onClick={onRecenter}
          title="Re-centre on the school"
          className="map-chip text-secondary hover:text-primary pointer-events-auto absolute top-3.5 left-4 cursor-pointer text-[11px] leading-[1.3] transition-colors duration-(--duration-snap)"
        >
          {subject.address}
        </button>
      )}

      <span className="pointer-events-auto absolute top-3.5 right-4 flex items-center gap-1.5">
        {drive && <span className="map-chip notation">{drive.label}</span>}
        {office && !directions && (
          <button
            type="button"
            onClick={onDirections}
            className="map-chip text-brand-secondary hover:text-brand-secondary_hover text-2xs cursor-pointer font-mono leading-none font-medium transition-colors duration-(--duration-snap)"
          >
            directions ↗
          </button>
        )}
      </span>

      {directions && office && drive && (
        <DirectionsPanel
          subject={subject}
          office={office}
          chosen={chosen}
          drive={drive}
          onOrigin={onOrigin}
          onClose={onClose}
        />
      )}
    </div>
  )
}

/*
 * Directions start at your office. "From" is seeded from the job's region,
 * never asked for, with one escape hatch — "change" lists the offices and
 * sets the origin for this visit only. The panel carries from → to and the
 * way out to Maps. A sheet with a print.900 header, like every panel that
 * floats on the map.
 */
function DirectionsPanel({
  subject,
  office,
  chosen,
  drive,
  onOrigin,
  onClose,
}: {
  subject: GroundSubject
  office: GroundOffice
  chosen: boolean
  drive: ReturnType<typeof driveEstimate>
  onOrigin: (office: GroundOffice) => void
  onClose: () => void
}) {
  const shown = useArrival(16)
  return (
    <div
      role="dialog"
      aria-label="Directions"
      className="sheet pointer-events-auto absolute top-3.5 left-4 w-[308px] overflow-hidden"
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(10px)",
        transition: `transform ${MOTION.panelIn}ms ${CSS_EASE_OUT}, opacity ${MOTION.panelIn}ms ${CSS_EASE_OUT}`,
      }}
    >
      <div className="bg-brand-section flex items-center justify-between gap-2.5 px-3.5 py-2.5">
        <span className="font-condensed text-[13px] leading-none font-semibold tracking-[-0.006em] text-stone-50">
          Directions
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-onprint-emphasis cursor-pointer font-mono text-[10.5px] leading-none hover:text-stone-50"
        >
          done ×
        </button>
      </div>
      <div className="flex flex-col gap-3 px-3.5 pt-3 pb-3.5">
        <Leg
          mark={
            <span aria-hidden="true" className="map-office map-office-sm" />
          }
          eyebrow="from · your office"
          title={office.label}
          note={
            <>
              {chosen ? "chosen for this visit" : "set from the job's region"}
              {" · "}
              {office.state}
              {subject.offices.length > 1 && (
                <>
                  {" · "}
                  <OfficeMenu
                    offices={subject.offices}
                    current={office}
                    onChoose={onOrigin}
                  />
                </>
              )}
            </>
          }
        />
        <Leg
          mark={<span aria-hidden="true" className="map-pin map-pin-sm" />}
          eyebrow="to · the job"
          title={subject.label}
          note={subject.address}
        />
      </div>
      <div className="bg-secondary_subtle border-ink-hairline flex items-center justify-between gap-2.5 border-t px-3.5 py-2.5">
        <span className="notation text-tertiary">{drive.label}</span>
        <a
          href={mapsDirectionsUrl(office, subject.point)}
          target="_blank"
          rel="noreferrer"
          className="text-brand-secondary hover:text-brand-secondary_hover text-2xs font-mono leading-none font-medium"
        >
          open in Maps ↗
        </a>
      </div>
    </div>
  )
}

/** The one escape hatch out of the seeded "from": a well listing the offices; the choice lasts the visit. */
function OfficeMenu({
  offices,
  current,
  onChoose,
}: {
  offices: GroundOffice[]
  current: GroundOffice
  onChoose: (office: GroundOffice) => void
}) {
  return (
    <Dropdown.Root>
      <Dropdown.Trigger className="text-brand-secondary hover:text-brand-secondary_hover outline-focus-ring cursor-pointer rounded-xs font-mono focus-visible:outline-2 focus-visible:outline-offset-2">
        change
      </Dropdown.Trigger>
      {/* non-modal: a modal popover would lock the page's scroll and shift the sheet */}
      <Dropdown.Popover isNonModal placement="bottom left" className="w-72">
        <Dropdown.Menu
          aria-label="Start the drive from"
          selectedKeys={[current.state]}
          onAction={(key) => {
            const office = offices.find((o) => o.state === key)
            if (office) onChoose(office)
          }}
        >
          {offices.map((office) => (
            <Dropdown.Item
              key={office.state}
              id={office.state}
              label={office.label}
              addon={office.state}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  )
}

function Leg({
  mark,
  eyebrow,
  title,
  note,
}: {
  mark: React.ReactNode
  eyebrow: string
  title: string
  note: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-[3px] flex-none">{mark}</span>
      <span className="flex min-w-0 flex-col gap-[3px]">
        <span className="eyebrow text-[8.5px]">{eyebrow}</span>
        <span className="text-primary text-[12.5px] leading-[1.3] font-medium">
          {title}
        </span>
        {note && <span className="notation text-[9.5px]">{note}</span>}
      </span>
    </div>
  )
}
